import React, { useContext, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useLocation, useNavigate } from 'react-router-dom';
import MobileDashboardHeader from '../components/navbar/MobileDashboardHeader';
import Modal from '../components/Modal/mobileModal';
import Button from '../components/button/button';
import PlayButton from '../components/button/playButton';
import SidebarContext from '../contexts/MyContext';
import { convertToMp3, convertToWav, convertToCaf } from './RecordingFeatures/convertToMp3';
import { generateRandomNumber } from '../utils/generateRandomNumber';
import { formatTimeInHHMMSS } from '../utils/timeFile';
import { AudioHandler } from '../Apihander/AudioRecording/AudioRecording';
import { errorToast, successToast } from '../utils/notificationUtils';
import SettingHandler from '../Apihander/setting/settingHandler';
import { Icon } from '@iconify/react';
import '../css/app/recorder.css';

const featureList = [
  { key: 'trim', label: 'Trim', icon: '/img/mobileIcon/trim.png', tab: 3 },
  { key: 'merge', label: 'Merge', icon: '/img/mobileIcon/merge.png', tab: 4 },
  { key: 'convert', label: 'Convert', icon: '/img/mobileIcon/convert.png', tab: 5 },
  { key: 'transcribe', label: 'Transcribe', icon: '/img/mobileIcon/transcript.png', tab: 6 },
];

// Presentation only: lucide icon and tone per feature.
const featureIcons = { trim: 'lucide:scissors', merge: 'lucide:merge', convert: 'lucide:refresh-cw', transcribe: 'lucide:file-text' };
const featureTones = { trim: 'vr-tone-cyan', merge: 'vr-tone-blue', convert: 'vr-tone-violet', transcribe: 'vr-tone-indigo' };

const getStoreLinks = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  if (/android/i.test(ua)) {
    return {
      store: 'Get it from Google Play',
      url: 'https://play.google.com/store/apps/details?id=com.voicerecorder.memo&hl=en_US&gl=US&pli=1',
      icon: '/img/icon/play-store.png',
    };
  }
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
    return {
      store: 'Get it from App Store',
      url: 'https://apps.apple.com/in/app/recording-app/id1537387586',
      icon: '/img/icon/app-store.png',
    };
  }
  return null;
};

const widgetId = 81000001805;

const MobileDashboard = () => {
  const {
    isRecording,
    // elapsedTime,
    // pauseRecording,
    // stopRecording,
    resetRecording,
    setBtnToggl,
    btntoggl,
    setRecordNew,
    recordNew,
    setTabIndex,
    tabIndex,
    setActionTabIndex,
    user,
    // setLoading,
    // load,
    selectedFormat,
    setFilterAudioData,
    setSelectedFormat
  } = useContext(SidebarContext);

  const [showModal, setShowModal] = React.useState(false);
  const [modalFeature, setModalFeature] = React.useState(null);
  const [audioName, setAudioName] = useState("");
  const [elapsedTime, setElapsedTime] = useState(0);
  const [recordingStart, setRecordingStart] = useState(false);
  const [load, setLoading] = useState(false)

  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const shouldUploadRef = useRef(true);
  const elapsedTimeRef = useRef(0);
  const intervalRef = useRef(null);

  const navigate = useNavigate()

  useEffect(() => {
    // Only add the widget if not already present
    if (!window.fwSettings) {
      window.fwSettings = { widget_id: widgetId };
      const script = document.createElement('script');
      script.src = 'https://ind-widget.freshworks.com/widgets/81000001805.js';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        const checkIframe = setInterval(() => {
          const iframe = document.getElementById('launcher-frame');
          if (iframe) {
            iframe.style.bottom = '70px';
            iframe.style.right = '20px';
            clearInterval(checkIframe);
          }        
        }, 100);
      };

      document.body.appendChild(script);

      // Cleanup on unmount
      // return () => {
      //   const freshdeskButton = document.querySelector('iframe.fw-support-icon');
      //   if (freshdeskButton) {
      //     freshdeskButton.remove();
      //   }
      //   document.body.removeChild(script);
      //   delete window.fwSettings;
      // };
    }
  }, []);

  useEffect(()=>{
    getSettingDetails()
  }, [])



  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);


  const getSettingDetails = async () => {
    try {
      const data = await SettingHandler.doGetSetting();

      if (data?.status) {
        setSelectedFormat(data?.data?.audioFormat);
      }
    } catch (error) {
      console.log("🚀 ~ getSetting ~ error:", error);
    }
  };

  // Start Recording logic
  const handleStart = () => {
    setRecordNew(true);
    setBtnToggl(false);
    resetRecording();
    setTimeout(() => setBtnToggl(false), 100); // mimic dashboard logic
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      // setIsRecording(false);
      clearInterval(intervalRef.current);
      setElapsedTime(elapsedTimeRef.current);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.pause();
        // setIsPaused(true);
        clearInterval(intervalRef.current);
        // console.log('Recording paused...');
      } else if (mediaRecorderRef.current.state === 'paused') {
        mediaRecorderRef.current.resume();
        // setIsPaused(false);
        intervalRef.current = setInterval(() => {
          elapsedTimeRef.current += 1;
          setElapsedTime(elapsedTimeRef.current);
        }, 1000);
        // console.log('Recording resumed...');
      }
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/mp4' });
      mediaStreamRef.current = stream;
      audioChunksRef.current = []; // Reset audio chunks at the start of recording

      mediaRecorderRef.current.ondataavailable = event => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach((track) => track.stop());
          mediaStreamRef.current = null; // Clear the MediaStream
        }
        // Check the flag to determine if upload should be skipped
        if (!shouldUploadRef.current) {
          shouldUploadRef.current = true; // Reset flag for the next recording
          return; // Skip upload
        }


        if (audioChunksRef.current.length === 0) {
          console.error("No audio chunks available for creating audio blob.");
          return;
        }

        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mp4' });
          audioChunksRef.current = [];

          let processedBlob;
          try {

            // processedBlob = await convertToMp3(audioBlob);


            if (selectedFormat == "mp3") {
              processedBlob = await convertToMp3(audioBlob);
            } else if (selectedFormat == "wav") {
              processedBlob = await convertToWav(audioBlob);
            } else if (selectedFormat == "caf") {
              processedBlob = await convertToCaf(audioBlob);
            } else if (selectedFormat == "m4a") {
              // processedBlob = await convertToM4a(audioBlob);
              processedBlob = audioBlob;

            } else {
              throw new Error("Unsupported audio format");
            }

            const randomAudioName = `Voice${generateRandomNumber()}`;
            setAudioName(randomAudioName);

            await doHandalUploadAudioUrl(processedBlob, randomAudioName, elapsedTimeRef.current);
          } catch (conversionError) {
            console.error("Audio conversion error:", conversionError);
          }
        } catch (blobError) {
          console.error("Error creating audio blob:", blobError);
        } finally {
          clearInterval(intervalRef.current);
          elapsedTimeRef.current = 0;
          setElapsedTime(0);
        }
      };

      mediaRecorderRef.current.start();
      // setIsRecording(true);
      elapsedTimeRef.current = 0;

      intervalRef.current = setInterval(() => {
        elapsedTimeRef.current += 1;
        setElapsedTime(elapsedTimeRef.current);


        if (elapsedTimeRef.current >= 300) {
          if (user?.isSubscriptionActive) {
            if (mediaRecorderRef.current.state === 'paused') {
              mediaRecorderRef.current.resume();
            }
            // setIsRecording(true);
            // setIsPaused(false);
          } else {
            mediaRecorderRef.current.pause();
            stopRecording();
            // setIsRecording(false);
            // setIsPaused(true);
            clearInterval(intervalRef.current);
            navigate('/pricing');
          }
        }
      }, 1000);

      // Cleanup function for component unmount
      return () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        stream.getTracks().forEach(track => track.stop());
      };
    } catch (err) {
      console.error('Failed to start recording', err);
      // setIsRecording(false);
      // setIsPaused(false);
    }
  };

  const doHandalUploadAudioUrl = async (audioBlob, audioName, elapsedTime) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('audiofile', audioBlob, `${audioName}.${selectedFormat}`);
      formData.append('timeDuration', formatTimeInHHMMSS(elapsedTime));
      formData.append('action', 'Audio');
      formData.append('isFavourite', false)

      const data = await AudioHandler.doUploadAudioUrl(formData);
      if (data?.statusCode === 200) {
        successToast(data?.message);
        setRecordingStart(false);
        setBtnToggl(false);

        // Add the new audio data to the existing array
        setFilterAudioData((prevFilterAudioData) => [
          ...prevFilterAudioData,
          data?.data,
        ]);
        setTabIndex(2);
        setLoading(false)
        setElapsedTime(0); // Reset the elapsed time
        elapsedTimeRef.current = 0;


      } else {
        const errorMessage =
          data?.response?.data?.error ||
          data?.response?.data?.message ||
          data.message ||
          "An error occurred";
        errorToast(errorMessage);
        setLoading(false)

      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "An error occurred";
      errorToast(errorMessage);
      setLoading(false)
      // setIsRecording(false)
      setElapsedTime(0)

    } finally {
      setLoading(false);
    }
  };

  // Pause logic
  const handlePause = () => {
    setBtnToggl(true);
    pauseRecording();
  };

  // Resume logic
  const handleResume = () => {
    setBtnToggl(false);
    pauseRecording();
  };

  // Stop logic
  const handleStop = () => {
    if (elapsedTime >= 1) {
      setRecordNew(true);
      stopRecording();
      setLoading && setLoading(true);
    }
  };

  const handleFeatureClick = (feature) => {
    setModalFeature(feature);
    setShowModal(true);
  };

  const handleTabOption = (feature) => {
    setTabIndex(feature.tab);
    setShowModal(false);
    setActionTabIndex(feature.tab);
  };


  const formatTime = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, '0');
    const s = String(sec % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  const store = getStoreLinks();

  if (!isMobile) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>This page is only available on mobile devices.</div>;
  }

  // Visual state only: live while recording, paused after Pause.
  const orbState = !recordingStart ? '' : btntoggl ? ' is-paused' : ' is-live';

  return (
    <div className={`vr-mdash${orbState}`}>

      <MobileDashboardHeader 
        showPricing={true}
        showDashboardNavigation={false} />
      {/* <MobileDashboardHeaderNew/> */}

      <div className="vr-mdash-stage">

        <div className="vr-mdash-hero">
          {/* Central recording orb; rings pulse only while recording */}
          <div className={`vr-orb vr-mdash-orb${!recordingStart || btntoggl ? '' : ' is-live'}`} role="img" aria-label="Voice-Recorder-Frequency">
            <Icon icon="lucide:audio-lines" width={44} height={44} />
          </div>
          {/* Timer Display */}
          <div className="vr-mdash-timer">
            {/* {recordNew ? `${formatTime(elapsedTime)}${!user?.isSubscriptionActive || !user ? ' / 05:00' : ''}` : ''} */}
            {recordingStart ? `${formatTime(elapsedTime)}${!user?.isSubscriptionActive || !user ? ' / 05:00' : ''}` : ''}
          </div>
        </div>
        {/* Show only Start button before recording, then show Pause/Resume and Stop */}
        {!recordingStart ? (
          <div className="vr-mdash-controls vr-mbtn-wrap is-primary is-start" style={{ marginTop: recordingStart ? "20px" : "30px" }} >
            <PlayButton
              // border={true}
              // click={handleStart}
              click={() => { setRecordingStart(true); startRecording() }}
              src={"/img/button/music.png"}
              btnText={"Start Recording"}
              MainStartRecording={true}
            />
          </div>
        ) : (
          <>
            <div className="vr-mdash-controls vr-mdash-row">
              {btntoggl ? (
                <button
                  className="vr-mbtn is-secondary is-resume"
                  onClick={handleResume}
                >
                  <img src={"/img/button/Vector.png"} alt={"Resume"} />
                  Resume
                </button>
              ) : (
                <div className="vr-mbtn-wrap is-secondary">
                  <PlayButton
                    border={true}
                    // click={handlePause}
                    click={() => {
                      if (!load) {
                        setBtnToggl(true); pauseRecording()
                      }
                    }}
                    src={"/img/button/push.png"}
                    btnText={"Pause"}
                  />
                </div>
              )}
              <div className={`vr-mbtn-wrap is-primary${load ? ' is-loading' : ''}`}>
                <PlayButton
                  // click={handleStop}
                  click={(e) => {
                    if (elapsedTime >= 1) {
                      // setRecordingStart(true);
                      // setRecordingStart(false);
                      stopRecording();
                      // setOpenModal(true);
                      setLoading(true)
                    }
                  }
                  }
                  src={load ? "" : "/img/button/stop.png"}
                  btnText={load ? (
                    <div className="spinner-border" role="status" style={{ width: "1.3rem", height: "1.3rem" }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : "Stop"}
                />
              </div>
            </div>
          </>
        )}

      </div>

      {/* Bottom Feature Bar */}
      <nav className="vr-mdash-tabbar">
        {featureList.map(f => (
          <div
            key={f.key}
            className={`vr-mdash-tab ${featureTones[f.key]}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}
            onClick={() => handleFeatureClick(f)}
          >
            <span className="vr-mdash-tab-icon" role="img" aria-label={f.label}><Icon icon={featureIcons[f.key]} width={22} height={22} /></span>
            <span className="vr-mdash-tab-label">{f.label}</span>
          </div>
        ))}
      </nav>
      {/* Modal Dialog for Features */}
      {showModal && (
        <Modal onClose={() => setShowModal(false)} modaltxt={null}>
          <div className="vr-mfeature">
            <div className={`vr-icon-tile is-solid vr-mfeature-icon ${featureTones[modalFeature.key]}`} role="img" aria-label={modalFeature.label}>
              <Icon icon={featureIcons[modalFeature.key]} width={28} height={28} />
            </div>
            <div className="vr-mfeature-title">{modalFeature.label} Audio</div>
            <div className="vr-mfeature-text">
              This feature is available in our mobile app. Download now to access all advanced features!
            </div>
            <div className="vr-mfeature-actions">
              {store && (
                <a href={store.url} target="_blank" rel="noopener noreferrer" className="vr-mfeature-store">
                  <img src={store.icon} alt={store.store} />
                  {store.store}
                </a>
              )}
              <button
                className="vr-mfeature-dismiss"
                onClick={() => setShowModal(false)}
              >
                Not Now
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MobileDashboard; 